import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { isListingExpired } from '#/modules/listing/helpers/listingTiming';
import { listingApiServerFetchInternship } from '#/modules/listing/internships/api/listingApiFetchInternship';
import { viewInternshipReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { DynamicSearchInternshipRefinements } from '#/modules/search/internships/components/Refinements/DynamicSearchInternshipRefinements';
import { userDashboardApiServerFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewInternshipPage } from './ViewInternshipPage';

const DynamicExpiredInternshipPage = dynamic(
  () =>
    import('#/modules/listing/internships/pages/expired/ExpiredInternshipPage').then(
      (m) => ({ default: m.ExpiredInternshipPage }),
    ),
  { strategy: 'always' },
);

const DynamicUnpublishedInternshipPage = dynamic(
  () =>
    import('#/modules/listing/internships/pages/unpublished/UnpublishedInternshipPage').then(
      (m) => ({ default: m.UnpublishedInternshipPage }),
    ),
  { strategy: 'always' },
);

export const handle = {
  searchRefinements: <DynamicSearchInternshipRefinements />,
} satisfies LayoutHandleElements;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  context,
  params: { internshipIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewInternshipReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const sharedHandle = {
    defaultSearchType: 'INTERNSHIP',
    showDesktopSearch: true,
    showSearchRefinements: true,
    hideFooterDivider: true,
  } satisfies LayoutHandleProps;

  const [internshipId] = internshipIdDashSlug.split('-');

  try {
    const [internship, listingExtras] = await Promise.all([
      listingApiServerFetchInternship({ request, context })(internshipId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiServerFetchListingExtras({
            request,
            context,
          })([internshipId])
        : null,
    ]);

    const { saved, applicationId } = listingExtras?.[internshipId] ?? {
      saved: false,
      applicationId: null,
    };

    switch (internship.type) {
      case 'INTERNSHIP': {
        return {
          type: isListingExpired({
            expires: internship.expires,
            now: serverContext.now,
          })
            ? 'EXPIRED_INTERNSHIP'
            : internship.type,
          internship,
          locale: serverContext.locale,
          handle: {
            ...sharedHandle,
            searchFromPageType: 'LISTING',
            urlByLocale: internship.url,
          } satisfies LayoutHandleProps,
          applicationId,
          saved,
          now: serverContext.now,
        } as const;
      }
      case 'UNPUBLISHED_INTERNSHIP':
        return idealistNotFound({
          type: internship.type,
          unpublishedInternship: internship,
          handle: sharedHandle,
        } as const);
    }
  } catch {
    return idealistNotFound({
      type: 'NOT_FOUND',
      handle: sharedHandle,
    } as const);
  }
}

export default function ViewInternshipHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  switch (loaderData.type) {
    case 'NOT_FOUND':
      return <DynamicNotFoundPage />;
    case 'INTERNSHIP':
      return (
        <RouteModule
          title={`${loaderData.internship.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['INTERNSHIP']}`}
          ogImage={
            loaderData.internship.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data:
                    loaderData.internship.orgLogoOverride ??
                    loaderData.internship.org?.logo,
                })
          }
          description={loaderData.internship.descriptionTruncated.slice(0, 200)}
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.internship.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.internship.url[loaderData.internship.locale],
          }}
          schemaMarkups={[
            loaderData.internship.schemaInternshipMarkup,
            loaderData.internship.schemaBreadcrumbListMarkup?.[
              loaderData.locale
            ],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.internship}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewInternshipPage
                internship={loaderData.internship}
                listingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                listingApplicationFailure={listingApplicationFailure}
                now={loaderData.now}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    case 'EXPIRED_INTERNSHIP':
      return (
        <RouteModule title={loaderData.internship.name}>
          <DynamicExpiredInternshipPage
            internship={loaderData.internship}
            listingApplicationId={loaderData.applicationId}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_INTERNSHIP':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <DynamicUnpublishedInternshipPage
            unpublishedInternship={loaderData.unpublishedInternship}
          />
        </RouteModule>
      );
  }
}
